import { Component, OnInit, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalConfig, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { ShopOffersItem } from '@app/shared/models/shop-offers.model';
import { Display, FontSizeRescalerService } from '@app/shared/services/font-rescaler.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { ProgressBagEvents } from '../progressbag-events.service';
import { Observable } from 'rxjs';
import { DisplayComponent } from '@app/shared/components/display/display.component';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { SharedModule } from '@app/shared/shared.module';
import { EventsRepository } from '@app/shared/state/events/events.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-progressbag-start-modal',
  templateUrl: './progressbag-start.component.html',
  styleUrls: ['./progressbag-start.component.scss'],
  standalone: true,
  imports: [DisplayComponent, AnimatedTimerComponent, ButtonComponent, SharedModule],
})
export class ProgressbagStartModalComponent implements OnInit {
  componentDestroy: () => Observable<unknown>;

  protected readonly progressbagStartModalData = inject<ModalConfig>(MAT_DIALOG_DATA);
  private eventManager = inject(EventManager);
  public logger = inject(LoggerService);
  private fontSizeRescalerService = inject(FontSizeRescalerService);
  private translateService = inject(TranslateService);
  private progressBagService = inject(ProgressBagEvents);
  private eventsRepo = inject(EventsRepository);

  event = signal<LimitedTimeEvent | null>(null);
  lang = signal<string>('');
  progressbagShopItem = signal<ShopOffersItem | null>(null);
  progressbagMinPurchase = signal<string | null>(null);
  progressbagCurrencySymbol = signal<string | null>(null);
  maxPrize = signal<number>(0);

  ngOnInit(): void {
    const evt = this.eventsRepo.getEventById(this.progressbagStartModalData.properties.eventId);

    this.event.set(evt);
    this.lang.set(this.translateService.currentLang);

    const max = this.progressBagService.roundedInt(this.calculateMaxPrize(), 2);
    this.maxPrize.set(max);
  }

  closeModal(): void {
    this.eventManager.closeModal(
      new Modal(this.progressbagStartModalData.id, ModalType.Persisted, this.progressbagStartModalData)
    );
  }

  openProgressbag(): void {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.ProgressbagModalComponent, ModalType.View, {
        modal: this.progressbagStartModalData,
        animated: false,
      })
    );

    this.closeModal();
  }

  setFontSize(): string {
    return this.fontSizeRescalerService.setFontSize(Display.LargeDisplay, this.maxPrize());
  }

  calculateMaxPrize(): number {
    const evt = this.event();
    if (!evt) return 0;

    return evt.properties.recurring * this.progressbagStartModalData.properties.maxMultiplier;
  }
}
